import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import "moment/locale/de";

import config from "../../config/api.config";
import {
  DocumentationSliceState,
  PropertyArea,
  PropertyAreaAdjustedMonitors,
  PropertyAreaDeficiencies,
  Technician,
} from "../../store/documentation-slice";
import { Deficiency } from "../../types/deficiencies";
import { MinimalProduct } from "../../types/product";

import delexLogo from "./delexLogo.png";
import fmLogo from "./fm-logo.png";
import pdfImage from "./PDFImage.png";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-normal.woff"), // Regular
      fontWeight: "normal",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-500-normal.woff"), // Bold
      fontWeight: "medium",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-italic.woff"), // Italic
      fontStyle: "italic",
      fontWeight: "normal",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 20,
    paddingBottom: 80,
    fontFamily: "Roboto",
  },
  normalPage: {
    flexDirection: "column",
    padding: 20,
    paddingBottom: 80,
    fontFamily: "Roboto",
  },
  title: {
    fontSize: 7,
    flexDirection: "column",
    display: "flex",
    marginBottom: 4,
    color: "#8493AB",
  },
  floorName: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 4,
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 14,
  },
  name: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 4,
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 11,
  },
  signatureName: {
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 14,
  },
  deficiencyName: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 22,
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 14,
  },
  documentName: {
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 18,
    marginTop: 96,
    marginBottom: 24,
  },
  text: {
    flexDirection: "column",
    display: "flex",
    color: "#3d5170",
    fontSize: 11,
    minWidth: 30,
    fontWeight: "medium",
  },
  address: {
    marginBottom: 4,
    color: "#3d5170",
    fontSize: 9,
    minWidth: 30,
    flexWrap: "wrap",
    wordWrap: "break-word",
    overflowWrap: "break-word",
  },
  tableTitle: {
    fontSize: 7,
    flexDirection: "column",
    display: "flex",
    minWidth: 90,
    marginBottom: 5,
    color: "#8493AB",
  },
  tableTitleProduct: {
    fontSize: 7,
    flexDirection: "column",
    display: "flex",
    marginBottom: 5,
    color: "#8493AB",
  },
  tableText: {
    flexDirection: "column",
    display: "flex",
    minWidth: 90,
    marginBottom: 5,
    color: "#3d5170",
    fontSize: 9,
  },
  tableTextProduct: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 5,
    color: "#3d5170",
    fontSize: 9,
  },
  monitorType: {
    color: "#3d5170",
    fontSize: 9,
  },
  findings: {
    fontSize: 11,
    color: "#3d5170",
    marginTop: 22,
    marginBottom: 6,
    fontWeight: "medium",
  },
  tableTextReason: {
    fontWeight: "normal",
    fontStyle: "italic",
    flexDirection: "column",
    display: "flex",
    minWidth: 90,
    marginTop: 5,
    marginBottom: 10,
    color: "#8493AB",
    fontSize: 9,
  },
  information: {
    flexDirection: "row",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  remarkBox: {
    marginTop: 8,
    width: "auto",
  },
  section: {
    margin: 10,
    padding: 10,
    flexDirection: "column",
    flexGrow: 1,
  },
  recommendation: {
    fontSize: 9,
    color: "#3d5170",
  },
  cardContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    display: "flex",
    gap: 10,
  },
  container2: {
    marginTop: 22,
  },
  container3: {
    marginBottom: 22,
  },
  container: {
    marginBottom: 22,
    marginTop: 6,
  },
  card: {
    width: "calc(25% - 20px)",
    flexDirection: "column",
    display: "flex",
    gap: 5,
  },
  tableHeader: {
    flexDirection: "row",
    display: "flex",
    gap: 10,
    marginTop: 3,
    justifyContent: "flex-start",
    borderBottomWidth: 0.4,
    borderBottomColor: "#8493AB",
  },
  tableRow: {
    flexDirection: "row",
    display: "flex",
    gap: 10,
    justifyContent: "flex-start",
    borderBottomWidth: 0.4,
    borderBottomColor: "#8493AB",
  },
  tableRowMonitor: {
    flexDirection: "row",
    display: "flex",
    gap: 10,
    justifyContent: "flex-start",
  },
  productsContainer: {
    marginTop: 24,
    marginBottom: 24,
  },
  products: {
    marginTop: 10,
    width: "100%",
  },
  productsRow: {
    flexDirection: "row",
    paddingBottom: 2,
  },
  qty: {
    width: 60,
  },
  pName: {
    flexShrink: 1,
    flexGrow: 0,
    width: 230,
  },
  description: {
    flex: 1,
    flexWrap: "wrap",
  },
  signatureContainer: {
    width: "50%",
    height: 100,
    backgroundColor: "#EDEFF5",
    borderRadius: 10,
    padding: 10,
  },
  signaturePhoto: {
    width: "100%",
  },
  firmaText: {
    padding: 15,
    justifyContent: "center",
    fontSize: 8,
    width: "50%",
    marginBottom: 5,
    color: "#8493AB",
  },
  lastPart: {
    flexDirection: "row",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 22,
  },
  thickLine: {
    height: 4,
    backgroundColor: "#CED3DC",
    borderRadius: 2,
    width: "100%",
  },
  thickLine2: {
    height: 4,
    backgroundColor: "#CED3DC",
    borderRadius: 2,
    marginTop: 22,
    marginBottom: 22,
    width: "100%",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    height: 50,
    flexDirection: "row",
    display: "flex",
    justifyContent: "space-between",
    fontSize: 7,
    color: "#3d5170",
    paddingRight: 20,
    paddingLeft: 20,
  },
  nameContainer: {
    position: "absolute",
    bottom: 0,
    left: 10,
    right: 10,
  },
  line: {
    borderBottomWidth: 1,
    borderBottomColor: "#8493AB",
    width: "100%",
    marginTop: 5,
    marginBottom: 5,
  },
  footerTitle: {
    fontSize: 9,
    fontWeight: "medium",
    marginBottom: 4,
  },
  footerItem: {
    flexGrow: 1,
  },
  thirdItem: {
    flex: 1,
    textAlign: "left",
  },
  signature: {
    marginTop: 24,
  },
  fullPageImage: {
    width: "100%",
    height: "100%",
    maxHeight: 842,
  },
  imageContainer: {
    justifyContent: "flex-start",
    alignItems: "flex-end",
    width: "100%",
    height: 70,
  },
  image: {
    width: 100,
    height: "100%",
    objectFit: "contain",
  },
  infoRow: {
    flexDirection: "row",
    width: "100%",
    marginBottom: 20,
    gap: 10,
  },
  infoBox: {
    width: "50%",
  },
  pNameWrapper: {
    flexDirection: "column",
  },
  payExtraText: {
    fontSize: 7,
    color: "#5C7499",
    marginBottom: 3,
  },
});

type DocumentationProps = {
  documentation: DocumentationSliceState;
};

const typeMappings: { [key: string]: string } = {
  infestation: "Befall",
  noInfestation: "kein Befall",
  maintenancePerformed: "Pflege",
  unreachableOrMissing: "nicht erreichbar",
  notInspected: "nicht kontrolliert",
};

export const deficiencyTypeMappings: { [key: string]: string } = {
  hygienic: "Hygienemangel",
  facility: "Baumangel",
  organizational: "Organisatorischer Mangel",
  other: "Sonstiges",
};

const deficiencySubtypeMappings: { [key: string]: string } = {
  wallHole: "Loch vorhanden",
  jointDefective: "Fuge defekt",
  doorGateSealDefective: "Tür-/Torabschluss defekt",
  wallFloorConnectionInsufficient: "Wand-/Bodenanschluss unzureichend",
  wallFloorCoveringDefective: "Wand-/Bodenbelag defekt",
  fecesTraces: "Kotspuren vorhanden",
  deadAnimals: "Tote Tiere vorhanden",
  productResidues: "Produktreste vorhanden",
  eatenGoods: "Ware angefressen",
  cobwebs: "Gespinste vorhanden",
  pruningRequired: "Grünschnitt erforderlich",
  areaNotAccessible: "Bereich nicht zugänglich",
  doorGateOpen: "Tür/Tor steht offen",
  contaminationPresent: "Verschmutzung vorhanden",
};

const monitorUpdated = "Monitor angepasst";
const monitorRemoved = "Monitor entfernt";
const logoUrl =
  config.companyName === "F+M Schädlingsbekämpfungs GmbH" ? fmLogo : delexLogo;

const Footer = () => (
  <View style={styles.footer} fixed>
    <View style={styles.footerItem}>
      <Text style={styles.footerTitle}>{config.companyName}</Text>
      <Text style={styles.title}>{config.companyAddress}</Text>
      <Text style={styles.title}>{config.companyCity}</Text>
    </View>

    <View style={[styles.footerItem, styles.thirdItem]}>
      <Text style={styles.title}>Seite</Text>
      <Text
        style={styles.footerTitle}
        render={({ pageNumber, totalPages }) =>
          `${pageNumber} von ${totalPages}`
        }
        fixed
      />
    </View>
  </View>
);

export const PDFDocumentForm: React.FC<DocumentationProps> = ({
  documentation,
}) => {
  const appointmentType =
    documentation.appointmentSubset.typeOfAppointment === "auftrag"
      ? "Bekämpfung"
      : documentation.appointmentSubset.typeOfAppointment === "other"
        ? "Sonstiges"
        : documentation.appointmentSubset.typeOfAppointment;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <View style={styles.imageContainer}>
            <Image src={logoUrl} style={styles.image} />
          </View>
          <View style={styles.information}>
            <View style={{ flex: 1, flexDirection: "column" }}>
              <Text style={styles.title}>Kunde</Text>
              <Text style={styles.name}>
                {documentation.appointmentSubset.customerName}
              </Text>
              {documentation.appointmentSubset.objectName && (
                <Text style={styles.address}>
                  {documentation.appointmentSubset.objectName}
                </Text>
              )}
              <Text style={styles.address}>
                {documentation.appointmentSubset.street}
              </Text>
              <Text style={styles.address}>
                {documentation.appointmentSubset.postcode}{" "}
                {documentation.appointmentSubset.city}
              </Text>
            </View>
          </View>

          <Text style={styles.documentName}>Zusammenfassung des Einsatzes</Text>

          <View style={styles.information}>
            <View>
              <Text style={styles.title}>Leistung</Text>
              {appointmentType ? (
                <Text style={styles.text}>
                  {appointmentType.charAt(0).toUpperCase() +
                    appointmentType.slice(1)}
                </Text>
              ) : null}
            </View>

            <View>
              <Text style={styles.title}>Datum</Text>
              <Text style={styles.text}>{documentation.date}</Text>
            </View>

            <View>
              <Text style={styles.title}>Arbeitsaufwand</Text>
              <Text style={styles.text}>{documentation.totalTimeWorkload}</Text>
            </View>

            <View>
              <Text style={styles.title}>Verantwortlicher Techniker</Text>
              {documentation.appointmentSubset.technicians.length > 0
                ? documentation.appointmentSubset.technicians.map(
                    (technician: Technician, index: number) => (
                      <View key={index}>
                        <Text style={styles.text}>
                          {technician.firstName} {technician.lastName}
                        </Text>
                      </View>
                    )
                  )
                : null}
            </View>
          </View>

          <View style={styles.thickLine} />

          <View style={styles.productsContainer}>
            <Text style={styles.name}>Materialeinsatz</Text>
            <View style={styles.products}>
              <View style={styles.productsRow}>
                <Text style={[styles.tableTitleProduct, styles.qty]}>
                  Menge
                </Text>
                <Text style={[styles.tableTitleProduct, styles.pName]}>
                  Artikel
                </Text>
                <Text style={[styles.tableTitleProduct, styles.description]}>
                  Beschreibung
                </Text>
              </View>
              {documentation.products.map((product: MinimalProduct) => (
                <View
                  key={product.id}
                  style={[
                    styles.productsRow,
                    {
                      borderTopWidth: 0.4,
                      borderTopColor: "#CED3DC",
                      paddingTop: 2,
                    },
                  ]}
                >
                  <Text style={[styles.tableTextProduct, styles.qty]}>
                    {product.quantity}
                  </Text>
                  <View style={styles.pNameWrapper}>
                    <Text style={[styles.tableTextProduct, styles.pName]}>
                      {product.name}
                    </Text>

                    {(product.payExtraQuantity ?? 0) > 0 && (
                      <Text style={styles.payExtraText}>
                        {product.payExtraQuantity} von {product.quantity} wurden
                        als kostenpflichtig markiert
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.tableTextProduct, styles.description]}>
                    {product.description ? product.description : "-"}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          <View break>
            <Text style={styles.name}>Bemerkungen</Text>

            <View style={styles.remarkBox}>
              {documentation.remark ? (
                <Text style={styles.recommendation}>
                  {documentation.remark}
                </Text>
              ) : (
                <Text style={styles.title}>Kein bemerkungsfeld</Text>
              )}
            </View>
          </View>
        </View>
        <Footer />
      </Page>
      <Page size="A4" wrap={true} style={styles.normalPage}>
        <View style={styles.section}>
          {documentation.propertyAreas.length > 0
            ? documentation.propertyAreas.map(
                (area: PropertyArea, index: number) => (
                  <View key={index}>
                    <Text style={styles.floorName}>{area.area}</Text>

                    <Text style={styles.findings}>Befunde</Text>

                    <View style={styles.cardContainer}>
                      {area.events.map((event: any, i: any) => (
                        <View style={styles.card} key={i}>
                          <Text style={styles.monitorType}>
                            {event.monitorType}
                          </Text>
                          <View style={styles.tableHeader}>
                            <Text style={styles.tableTitle}>
                              Monitor-Status
                            </Text>
                            <Text style={styles.tableTitle}>Anzahl</Text>
                          </View>
                          {Object.entries(typeMappings).map(
                            ([eventType, mapping], index) => {
                              const filteredEvents = event.events.filter(
                                (subEvent: any) =>
                                  subEvent.type === eventType &&
                                  subEvent.count !== null
                              );

                              if (filteredEvents.length > 0) {
                                return (
                                  <View style={styles.tableRow} key={index}>
                                    <Text style={styles.tableText}>
                                      {mapping}
                                    </Text>
                                    <Text style={styles.tableText}>
                                      {filteredEvents.map(
                                        (subEvent: any) => subEvent.count
                                      )}
                                    </Text>
                                  </View>
                                );
                              } else {
                                return null;
                              }
                            }
                          )}
                        </View>
                      ))}
                    </View>

                    {documentation.propertyAreaAdjustedMonitors.length > 0 &&
                      documentation.propertyAreaAdjustedMonitors.map(
                        (
                          adjustedMonitors: PropertyAreaAdjustedMonitors,
                          k: number
                        ) => (
                          <View key={k}>
                            {adjustedMonitors.label === area.area &&
                              adjustedMonitors.monitors.length > 0 &&
                              adjustedMonitors.monitors.map(
                                (monitor: any, h: number) => {
                                  const originalVersion = monitor.versions.find(
                                    (version: any) => version.isOriginal === 1
                                  );

                                  const changedVersion = monitor.versions.find(
                                    (version: any) => version.isOriginal === 0
                                  );

                                  return (
                                    <View key={h} style={styles.container2}>
                                      <Text style={styles.text}>Monitore</Text>
                                      <View style={styles.tableHeader}>
                                        <Text style={styles.tableTitle}>
                                          Monitorbezeichnung
                                        </Text>
                                        <Text style={styles.tableTitle}>
                                          Aktion
                                        </Text>
                                        <Text style={styles.tableTitle}>
                                          Neue Bezeichnung
                                        </Text>
                                        <Text style={styles.tableTitle}>
                                          Neue Position
                                        </Text>
                                      </View>
                                      <View
                                        style={styles.tableRowMonitor}
                                        key={h}
                                      >
                                        <Text
                                          style={[
                                            styles.tableText,
                                            { marginTop: 3 },
                                          ]}
                                        >
                                          {`${monitor.monitorType.prefix.toUpperCase()} ${
                                            monitor.isTemporary ? "T" : ""
                                          }${
                                            originalVersion
                                              ? originalVersion.position
                                              : monitor.position
                                          }`}
                                        </Text>
                                        <Text
                                          style={[
                                            styles.tableText,
                                            { marginTop: 3 },
                                          ]}
                                        >
                                          {changedVersion.action === "update"
                                            ? monitorUpdated
                                            : monitorRemoved}
                                        </Text>
                                        <Text
                                          style={[
                                            styles.tableText,
                                            { marginTop: 3 },
                                          ]}
                                        >
                                          {changedVersion.action === "update"
                                            ? `${monitor.monitorType.prefix.toUpperCase()} ${
                                                monitor.isTemporary ? "T" : ""
                                              }${monitor.position}`
                                            : "-"}
                                        </Text>
                                        <Text
                                          style={[
                                            styles.tableText,
                                            { marginTop: 3 },
                                          ]}
                                        >
                                          {changedVersion.action === "update"
                                            ? monitor.location
                                            : "-"}
                                        </Text>
                                      </View>
                                      <View
                                        style={[
                                          styles.tableRow,
                                          { fontStyle: "italic" },
                                        ]}
                                      >
                                        <Text
                                          style={[
                                            styles.tableTextReason,
                                            {
                                              width: "100%",
                                              fontWeight: "normal",
                                              fontStyle: "italic",
                                            },
                                          ]}
                                        >
                                          {`GRUND: ${
                                            changedVersion.reason
                                              ? changedVersion.reason
                                              : "-"
                                          }`}
                                        </Text>
                                      </View>
                                    </View>
                                  );
                                }
                              )}
                          </View>
                        )
                      )}

                    {documentation.propertyAreasDeficiencies.length > 0 &&
                      documentation.propertyAreasDeficiencies.map(
                        (deficiency: PropertyAreaDeficiencies, k: number) => (
                          <View key={k}>
                            {deficiency.area === area.area &&
                              deficiency.incidents.length > 0 && (
                                <View style={styles.container2}>
                                  <Text style={styles.text}>Mängel</Text>
                                  <View style={styles.tableHeader}>
                                    <Text
                                      style={[
                                        styles.tableTitle,
                                        { width: "25%" },
                                      ]}
                                    >
                                      Art des Mangels
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableTitle,
                                        { width: "25%" },
                                      ]}
                                    >
                                      Bezeichnung
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableTitle,
                                        { width: "25%" },
                                      ]}
                                    >
                                      Standortinformation
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableTitle,
                                        { width: "25%" },
                                      ]}
                                    >
                                      Empfehlungen
                                    </Text>
                                  </View>
                                </View>
                              )}
                            {deficiency.area === area.area &&
                              deficiency.incidents.length > 0 &&
                              deficiency.incidents.map(
                                (incident: Deficiency, h: number) => (
                                  <View style={styles.tableRow} key={h}>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { width: "25%" },
                                      ]}
                                    >
                                      {
                                        deficiencyTypeMappings[
                                          incident.deficiencyType
                                        ]
                                      }
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { width: "25%" },
                                      ]}
                                    >
                                      {
                                        deficiencySubtypeMappings[
                                          incident.deficiencySubType
                                        ]
                                      }
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { width: "25%" },
                                      ]}
                                    >
                                      {incident.location
                                        ? incident.location
                                        : "-"}
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { width: "25%", fontStyle: "italic" },
                                      ]}
                                    >
                                      {incident.requiredActions &&
                                      incident.requiredActions.length > 0
                                        ? incident.requiredActions.join(", ")
                                        : "-"}
                                    </Text>
                                  </View>
                                )
                              )}
                          </View>
                        )
                      )}

                    <View style={styles.thickLine2} />
                  </View>
                )
              )
            : null}

          {documentation.propertyAreasDeficiencies
            .filter((deficiency: PropertyAreaDeficiencies) =>
              documentation.propertyAreas.some(
                (area: PropertyArea) => area.area !== deficiency.area
              )
            )
            .map((deficiency: PropertyAreaDeficiencies, k: number) => (
              <View key={k}>
                <Text style={styles.deficiencyName}>{deficiency.area}</Text>
                {documentation.propertyAreaAdjustedMonitors.length > 0 &&
                  documentation.propertyAreaAdjustedMonitors.map(
                    (
                      adjustedMonitors: PropertyAreaAdjustedMonitors,
                      k: number
                    ) => (
                      <View key={k}>
                        {adjustedMonitors.label === deficiency.area &&
                          adjustedMonitors.monitors.length > 0 &&
                          adjustedMonitors.monitors.map(
                            (monitor: any, h: number) => {
                              const originalVersion = monitor.versions.find(
                                (version: any) => version.isOriginal === 1
                              );

                              const changedVersion = monitor.versions.find(
                                (version: any) => version.isOriginal === 0
                              );

                              return (
                                <View key={h} style={styles.container3}>
                                  <Text style={styles.text}>Monitore</Text>
                                  <View style={styles.tableHeader}>
                                    <Text style={styles.tableTitle}>
                                      Monitorbezeichnung
                                    </Text>
                                    <Text style={styles.tableTitle}>
                                      Aktion
                                    </Text>
                                    <Text style={styles.tableTitle}>
                                      Neue Bezeichnung
                                    </Text>
                                    <Text style={styles.tableTitle}>
                                      Neue Position
                                    </Text>
                                  </View>
                                  <View style={styles.tableRowMonitor} key={h}>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { marginTop: 3 },
                                      ]}
                                    >
                                      {`${monitor.monitorType.prefix.toUpperCase()} ${
                                        monitor.isTemporary ? "T" : ""
                                      }${
                                        originalVersion
                                          ? originalVersion.position
                                          : monitor.position
                                      }`}
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { marginTop: 3 },
                                      ]}
                                    >
                                      {changedVersion.action === "update"
                                        ? monitorUpdated
                                        : monitorRemoved}
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { marginTop: 3 },
                                      ]}
                                    >
                                      {changedVersion.action === "update"
                                        ? `${monitor.monitorType.prefix.toUpperCase()} ${
                                            monitor.isTemporary ? "T" : ""
                                          }${monitor.position}`
                                        : "-"}
                                    </Text>
                                    <Text
                                      style={[
                                        styles.tableText,
                                        { marginTop: 3 },
                                      ]}
                                    >
                                      {changedVersion.action === "update"
                                        ? monitor.location
                                        : "-"}
                                    </Text>
                                  </View>
                                  <View
                                    style={[
                                      styles.tableRow,
                                      { fontStyle: "italic" },
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.tableTextReason,
                                        {
                                          width: "100%",
                                          fontWeight: "normal",
                                          fontStyle: "italic",
                                        },
                                      ]}
                                    >
                                      {`GRUND: ${
                                        changedVersion.reason
                                          ? changedVersion.reason
                                          : "-"
                                      }`}
                                    </Text>
                                  </View>
                                </View>
                              );
                            }
                          )}
                      </View>
                    )
                  )}
                <Text style={styles.text}>Mängel</Text>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableTitle, { width: "25%" }]}>
                    Art des Mangels
                  </Text>
                  <Text style={[styles.tableTitle, { width: "25%" }]}>
                    Bezeichnung
                  </Text>
                  <Text style={[styles.tableTitle, { width: "25%" }]}>
                    Standortinformation
                  </Text>
                  <Text style={[styles.tableTitle, { width: "25%" }]}>
                    Empfehlungen
                  </Text>
                </View>
                {deficiency.incidents.length > 0 &&
                  deficiency.incidents.map(
                    (incident: Deficiency, h: number) => (
                      <View style={styles.tableRow} key={h}>
                        <Text style={[styles.tableText, { width: "25%" }]}>
                          {deficiencyTypeMappings[incident.deficiencyType]}
                        </Text>
                        <Text style={[styles.tableText, { width: "25%" }]}>
                          {
                            deficiencySubtypeMappings[
                              incident.deficiencySubType
                            ]
                          }
                        </Text>
                        <Text style={[styles.tableText, { width: "25%" }]}>
                          {incident.location ? incident.location : "-"}
                        </Text>
                        <Text
                          style={[
                            styles.tableText,
                            { width: "25%", fontStyle: "italic" },
                          ]}
                        >
                          {incident.requiredActions &&
                          incident.requiredActions.length > 0
                            ? incident.requiredActions.join(", ")
                            : "-"}
                        </Text>
                      </View>
                    )
                  )}
                <View style={styles.thickLine2} />
              </View>
            ))}

          {documentation.propertyAreaAdjustedMonitors.length > 0 &&
            documentation.propertyAreaAdjustedMonitors
              .filter(
                (adjustedMonitor: PropertyAreaAdjustedMonitors) =>
                  !documentation.propertyAreas.some(
                    (area: PropertyArea) => area.area === adjustedMonitor.label
                  ) &&
                  !documentation.propertyAreasDeficiencies.some(
                    (deficiency: PropertyAreaDeficiencies) =>
                      deficiency.area === adjustedMonitor.label
                  )
              )
              .map(
                (
                  adjustedMonitors: PropertyAreaAdjustedMonitors,
                  index: number
                ) => (
                  <View key={index}>
                    {adjustedMonitors.monitors.map(
                      (monitor: any, h: number) => {
                        const originalVersion = monitor.versions.find(
                          (version: any) => version.isOriginal === 1
                        );

                        const changedVersion = monitor.versions.find(
                          (version: any) => version.isOriginal === 0
                        );

                        return (
                          <View key={h} style={styles.container2}>
                            <Text style={styles.deficiencyName}>
                              {adjustedMonitors.label}
                            </Text>
                            <Text style={styles.text}>Monitore</Text>
                            <View style={styles.tableHeader}>
                              <Text style={styles.tableTitle}>
                                Monitorbezeichnung
                              </Text>
                              <Text style={styles.tableTitle}>Aktion</Text>
                              <Text style={styles.tableTitle}>
                                Neue Bezeichnung
                              </Text>
                              <Text style={styles.tableTitle}>
                                Neue Position
                              </Text>
                            </View>
                            <View style={styles.tableRowMonitor} key={h}>
                              <Text
                                style={[styles.tableText, { marginTop: 3 }]}
                              >
                                {`${monitor.monitorType.prefix.toUpperCase()} ${
                                  monitor.isTemporary ? "T" : ""
                                }${
                                  originalVersion
                                    ? originalVersion.position
                                    : monitor.position
                                }`}
                              </Text>
                              <Text
                                style={[styles.tableText, { marginTop: 3 }]}
                              >
                                {changedVersion.action === "update"
                                  ? monitorUpdated
                                  : monitorRemoved}
                              </Text>
                              <Text
                                style={[styles.tableText, { marginTop: 3 }]}
                              >
                                {changedVersion.action === "update"
                                  ? `${monitor.monitorType.prefix.toUpperCase()} ${
                                      monitor.isTemporary ? "T" : ""
                                    }${monitor.position}`
                                  : "-"}
                              </Text>
                              <Text
                                style={[styles.tableText, { marginTop: 3 }]}
                              >
                                {changedVersion.action === "update"
                                  ? monitor.location
                                  : "-"}
                              </Text>
                            </View>
                            <View
                              style={[styles.tableRow, { fontStyle: "italic" }]}
                            >
                              <Text
                                style={[
                                  styles.tableTextReason,
                                  {
                                    width: "100%",
                                    fontWeight: "normal",
                                    fontStyle: "italic",
                                  },
                                ]}
                              >
                                {`GRUND: ${
                                  changedVersion.reason
                                    ? changedVersion.reason
                                    : "-"
                                }`}
                              </Text>
                            </View>
                          </View>
                        );
                      }
                    )}
                    <View style={styles.thickLine2} />
                  </View>
                )
              )}

          {documentation.propertyAreas.length === 0 &&
            documentation.propertyAreasDeficiencies.map(
              (deficiency: PropertyAreaDeficiencies, k: number) => (
                <View key={k}>
                  <Text style={styles.deficiencyName}>{deficiency.area}</Text>
                  <Text style={styles.text}>Mängel</Text>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.tableTitle, { width: "25%" }]}>
                      Art des Mangels
                    </Text>
                    <Text style={[styles.tableTitle, { width: "25%" }]}>
                      Bezeichnung
                    </Text>
                    <Text style={[styles.tableTitle, { width: "25%" }]}>
                      Standortinformation
                    </Text>
                    <Text style={[styles.tableTitle, { width: "25%" }]}>
                      Empfehlungen
                    </Text>
                  </View>
                  {deficiency.incidents.length > 0 &&
                    deficiency.incidents.map(
                      (incident: Deficiency, h: number) => (
                        <View style={styles.tableRow} key={h}>
                          <Text style={[styles.tableText, { width: "25%" }]}>
                            {deficiencyTypeMappings[incident.deficiencyType]}
                          </Text>
                          <Text style={[styles.tableText, { width: "25%" }]}>
                            {
                              deficiencySubtypeMappings[
                                incident.deficiencySubType
                              ]
                            }
                          </Text>
                          <Text style={[styles.tableText, { width: "25%" }]}>
                            {incident.location ? incident.location : "-"}
                          </Text>
                          <Text
                            style={[
                              styles.tableText,
                              { width: "25%", fontStyle: "italic" },
                            ]}
                          >
                            {incident.requiredActions &&
                            incident.requiredActions.length > 0
                              ? incident.requiredActions.join(", ")
                              : "-"}
                          </Text>
                        </View>
                      )
                    )}
                  <View style={styles.thickLine2} />
                </View>
              )
            )}

          <View break={true}>
            {documentation.roomRelease && documentation.measureRequired && (
              <View style={styles.infoRow}>
                <View style={styles.infoBox}>
                  <Text style={styles.title}>Raumfreigabe</Text>
                  <Text style={styles.name}>{documentation.roomRelease}</Text>
                </View>

                <View style={styles.infoBox}>
                  <Text style={styles.title}>
                    Bekämpfungsmaßnahme erforderlich
                  </Text>
                  <Text style={styles.name}>
                    {documentation.measureRequired}
                  </Text>
                </View>
              </View>
            )}
            <Text style={styles.signatureName}>Unterschrift</Text>
            <View style={styles.lastPart}>
              <View style={styles.signatureContainer}>
                {documentation.signature?.url && (
                  <Image
                    src={documentation.signature?.url}
                    style={styles.signaturePhoto}
                  />
                )}
                <View style={styles.nameContainer}>
                  <View style={styles.line} />
                  <Text style={styles.title}>{documentation.emailOwner}</Text>
                </View>
              </View>

              <Text style={styles.firmaText}>
                Die Firma {config.companyName} ist durch den o. g. Auftraggeber
                beauftragt, in o. g. Lieferanschrift die dokumentierten Arbeiten
                durchzuführen und/oder Präparate einzusetzen. Achtung! Über
                Gefährdungen- Sicherheits- und Reinigungsmaßnahmen, sowie die
                allgemeinen Hinweise wurde der Auftraggeber vor Beginn der
                Maßnahme aufgeklärt.
              </Text>
            </View>
          </View>
        </View>
        <Footer />
      </Page>
      <Page size="A4">
        <View
          style={{
            display: "flex",
            height: "100%",
            width: "100%",
            position: "relative",
          }}
        >
          <Image src={pdfImage} style={styles.fullPageImage} />
        </View>
      </Page>
    </Document>
  );
};
